import React, { useContext, useMemo } from 'react';
import { ActorRef, AnyEventObject, createMachine } from 'xstate';
import { useActor, useInterpret } from '@xstate/react';

import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { BannerMachine, DefaultActions } from './machine-config';
import { BannerError, BannerEvent, BannerState } from './types';

const BannerServiceContext = React.createContext<any | undefined>(undefined);

export interface IBannerServiceState {
  value: BannerState;
  context: {
    error?: BannerError;
  };
}

export interface IBannerService {
  state: IBannerServiceState;
  send: (event: BannerEvent, payload?: any) => void;
}

export function useBannerService(): IBannerService {
  const bannerService = useContext(BannerServiceContext);
  const [state] = useActor<ActorRef<AnyEventObject, IBannerServiceState>>(bannerService);

  return {
    state,
    send: bannerService!.send,
  };
}

export const BannerServiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const bannerMachine = useMemo(() => createMachine(BannerMachine, {
    actions: DefaultActions,
  }), []);
  const bannerService = useInterpret(bannerMachine);

  return (
    <BannerServiceContext.Provider value={bannerService}>
      {children}
    </BannerServiceContext.Provider>
  );
};

export function withBannerService<TProps>(
  WrappedComponent: React.ComponentType<TProps>,
): React.FC<TProps> {
  const HoC: React.FC<TProps> = (props: TProps) => (
    <BannerServiceProvider>
      <WrappedComponent {...props as any} />
    </BannerServiceProvider>
  );

  HoC.displayName = `withBannerService(${getDisplayName(WrappedComponent)})`;

  return HoC;
}
